import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Photo gallery override — applies the Stadium House PhotoGallery layout
 * (uniform responsive grid with rounded tiles + shared lightbox) to the
 * inherited GalleryApartments slot so existing pages keep working.
 */
export function GalleryApartments() {
  const images = useCollectionBinding<GalleryImage>('galleryapartments.images', {
    collection: 'photo_gallery',
    defaultLimit: 48,
  });

  const photoList = useMemo(() => {
    const list = images && images.length > 0 ? images : [];
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    return list.filter(
      (img) =>
        Boolean(img.image) &&
        !excludedTitles.has((img.title || '').toLowerCase().trim()),
    );
  }, [images]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (photoList.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained py-8 md:py-12">
        <div className="gallery-simple-grid">
          {photoList.map((img, index) => (
            <button
              key={img.image || index}
              type="button"
              onClick={() => setSelectedIndex(index)}
              className="gallery-simple-grid__cell group"
              aria-label={`Open photo ${index + 1}`}
            >
              <VibesitesImage
                url={img.image}
                alt={img.alt_text || img.title || ''}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                priority={index === 0}
              />
            </button>
          ))}
        </div>
      </div>

      <GalleryLightbox
        images={photoList}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        .gallery-simple-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        .gallery-simple-grid__cell {
          appearance: none;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
          overflow: hidden;
          border-radius: 5px;
          aspect-ratio: 4 / 3;
          display: block;
          width: 100%;
          animation: gallery-fade-in 0.35s ease both;
        }

        .gallery-simple-grid__cell:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 3px;
        }

        @media (max-width: 767px) {
          .gallery-simple-grid {
            gap: 0.5rem;
          }
        }

        @media (min-width: 640px) {
          .gallery-simple-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .gallery-simple-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-simple-grid__cell { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryApartments',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'galleryapartments.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 48,
      description: 'All photos displayed in the simple responsive grid.',
    },
  },
};

(GalleryApartments as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryApartments;
